import API from 'core/Api';
import { IMultipleDownloadRequest, IMultipleDownloadResponse, IArchive, IArchivesListResponse } from 'types/archives';
import { formatDate } from 'utils/helpers/formatters';
import { startArchivePolling } from 'utils/archivePolling';
import updateArchiveCount from 'utils/archiveCount';

export const extractErrorMessage = (error: any, fallbackMessage = 'An error occurred'): string => {
  const { response } = error || {};
  const { data } = response || {};

  if (data?.message) {
    return data.message;
  }

  if (data?.errors) {
    const { errors } = data;

    const errorEntry = Object.entries(errors).find(([, value]) => {
      return Array.isArray(value) && value.length > 0;
    });

    if (errorEntry) {
      const [, value] = errorEntry;

      if (Array.isArray(value) && value.length > 0) {
        const firstError = value[0];

        if (typeof firstError === 'object' && firstError !== null) {
          const nestedErrors = Object.values(firstError);
          if (nestedErrors.length > 0 && Array.isArray(nestedErrors[0])) {
            return nestedErrors[0][0] as string;
          }
        }

        if (typeof firstError === 'string') {
          return firstError;
        }
      }
    }
  }

  if (error?.message) {
    return error.message;
  }

  return fallbackMessage;
};

export const requestMultipleDownload = async (
  documentIds: number[],
  fromTab: string
): Promise<IMultipleDownloadResponse> => {
  const isDocumentsTab = fromTab === 'documents-tab';
  const response = await API.post<IMultipleDownloadRequest, { data: IMultipleDownloadResponse }>(
    'documents/multiple-download',
    {
      ...(isDocumentsTab ? { document_holder_ids: documentIds } : { document_ids: documentIds }),
      from_tab: fromTab,
    }
  );

  if (response.data.type === 'archive-download') {
    startArchivePolling();
  } else {
    await updateArchiveCount();
  }

  return response.data;
};

export const getArchives = async (): Promise<IArchivesListResponse> => {
  const response = await API.get<IArchivesListResponse>('archives');
  return response.data;
};

export const getArchiveDownloadLink = async (archiveId: number): Promise<string> => {
  const response = await API.get<{ download_url: string } | string>(`archives/${archiveId}/download`);

  if (typeof response.data === 'string') {
    return response.data;
  }

  return response.data.download_url;
};

export const downloadMultipleFiles = (links: string[]): void => {
  links.forEach((link, index) => {
    setTimeout(async () => {
      try {
        const response = await fetch(link);
        const blob = await response.blob();

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `file-${index + 1}.pdf`;

        // Try to decode filename from Content-Disposition header if present
        if (contentDisposition) {
          const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]*)/i);
          if (filenameStarMatch && filenameStarMatch[1]) {
            filename = decodeURIComponent(filenameStarMatch[1]);
          } else {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
              const rawFilename = filenameMatch[1].replace(/['"]/g, '');
              try {
                const bytes = new Uint8Array([...rawFilename].map(char => char.charCodeAt(0)));
                filename = new TextDecoder('utf-8').decode(bytes);
              } catch {
                filename = rawFilename;
              }
            }
          }
        }

        const blobUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = blobUrl;
        anchor.download = filename;
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();

        setTimeout(() => {
          document.body.removeChild(anchor);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
      } catch (error) {
        console.error('Download failed:', error);
        window.open(link, '_blank');
      }
    }, index * 300);
  });
};

export const formatArchiveType = (archiveType: string, t: (key: string) => string): string => {
  const typeMap: Record<string, string> = {
    'documents-tab': t('archives.types.documentsTab'),
    'leaves-tab': t('archives.types.leavesTab'),
    'reports-documents-tab': t('archives.types.reportsDocumentsTab'),
    'reports-document-actions-tab': t('archives.types.reportsDocumentActionsTab'),
    'health-and-safety-tab': t('archives.types.healthAndSafetyTab'),
    'work-trips-tab': t('archives.types.workTripsTab'),
  };

  return typeMap[archiveType] || archiveType;
};

export const formatArchiveStatus = (
  archive: IArchive,
  t: (key: string, options?: any) => string
): { text: string; isLink: boolean } => {
  switch (archive.status) {
    case 'pending':
      return {
        text: t('archives.statusPending'),
        isLink: false,
      };
    case 'generating':
      return {
        text: t('archives.statusGenerating', { time: formatDate(new Date(archive.created_at), 'dd.mm.YYY hh:ii') }),
        isLink: false,
      };
    case 'ready-for-download': {
      const formattedTime = formatDate(new Date(archive.expires_at), 'dd.mm.YYY hh:ii');
      return {
        text: t('archives.statusReadyForDownload', { time: formattedTime }),
        isLink: true,
      };
    }
    case 'expired':
      return {
        text: t('archives.statusExpired'),
        isLink: false,
      };
    default:
      return {
        text: archive.status,
        isLink: false,
      };
  }
};

export const isArchiveExpired = (archive: IArchive): boolean => {
  return archive.status === 'expired';
};
